import { CommonModule } from "@angular/common";
import { Component, OnInit, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { InputTextModule } from "primeng/inputtext";
import { FormsModule } from "@angular/forms";
import { LotSearchResults } from "../lot-search-results/lot-search.component";
import { firstValueFrom } from "rxjs";
import { BottomSheetComponent } from "../../../../../shared/components/bottom-sheet/bottom-sheet.component";
import { RadioButton } from "primeng/radiobutton";
import { SkeletonModule } from "primeng/skeleton";
import { NetworkStatusService } from "../../../../../shared/services/network-status.service";
import { HeaderComponentComponent as HeaderComponent } from "../../../../../shared/components/header-component/header-component.component";
import { DivisionDto } from "../../../../../domains/divisions/models/divisionDto";
import { DivisionService } from "../../../../../domains/divisions/services/division.service";
import { LotSearchService } from "../services/lot-search.service";

export interface Community {
  salesCommunityName: string;
  externalSalesCommunityId: number;
}

@Component({
  selector: "lot-community-search",
  standalone: true,
  imports: [
    CommonModule,
    InputTextModule,
    FormsModule,
    LotSearchResults,
    BottomSheetComponent,
    SkeletonModule,
    RadioButton,
    HeaderComponent,
  ],
  templateUrl: "./lot-community-search.component.html",
  styleUrl: "./lot-community-search.component.scss"
})
export class LotCommunitySearch implements OnInit {
  hasDetails = signal(false);

  isOpen = signal(false);
  isDivisionOpen = signal(false);

  selectedCommunity = signal<Community | undefined>(undefined);
  drawerSelectedCommunity: Community | undefined;
  drawerCommunityResults = signal<Community[]>([]);

  divisions = signal<DivisionDto[]>([]);
  selectedDivision = signal<DivisionDto | undefined>(undefined);
  drawerSelectedDivision: DivisionDto | undefined;
  disableDivisionDropdown = signal(false);

  loading = true;
  loadingDivisions = true;
  communities: Community[] = [];
  searchTerm = "";
  communitySearchTerm = "";
  isOnline = true;

  constructor(
    private lotSearchService: LotSearchService,
    private divisionService: DivisionService,
    private networkService: NetworkStatusService,
  ) {
    this.networkService.online$.pipe(takeUntilDestroyed()).subscribe(val => {
      this.isOnline = val;
    });
  }

  async ngOnInit(): Promise<void> {
    if (this.isOnline === false) {
      this.loading = false;
      this.loadingDivisions = false;
      return;
    }

    this.loading = true;
    this.loadingDivisions = true;

    const divisions = await firstValueFrom(this.divisionService.getDivisionsForUser());
    this.divisions.set(divisions ?? []);

    if (this.divisions().length === 1) {
      this.disableDivisionDropdown.set(true);
      await this.onDivisionSelected(this.divisions()[0]);
    } else {
      this.disableDivisionDropdown.set(false);
      this.selectedDivision.set(undefined);
      this.drawerSelectedDivision = undefined;
      this.communities = [];
      this.drawerCommunityResults.set([]);
      this.selectedCommunity.set(undefined);
      this.drawerSelectedCommunity = undefined;
    }

    this.loading = false;
    this.loadingDivisions = false;
  }

  async executeSearch(): Promise<void> {
    this.hasDetails.set(true);
  }

  async toCommunitySearch() {
    this.hasDetails.set(false);
  }

  openDivisionSearch(): void {
    if (this.isDivisionDisabled()) {
      return;
    }

    this.drawerSelectedDivision = this.selectedDivision();
    this.isDivisionOpen.set(true);
  }

  onDivisionCancel(): void {
    this.isDivisionOpen.set(false);
  }

  async onDivisionSelect(): Promise<void> {
    this.isDivisionOpen.set(false);
    await this.onDivisionSelected(this.drawerSelectedDivision);
  }

  openCommunitySearch() {
    if (this.isCommunityDisabled() || this.loading) {
      return;
    }

    this.isOpen.set(true);
  }

  onCancel() {
    this.isOpen.set(false);
  }

  onSelect() {
    this.selectedCommunity.set(this.drawerSelectedCommunity);
    this.isOpen.set(false);
  }

  onDrawerSearchTermChange(value: string) {
    this.communitySearchTerm = value;

    const results = this.communities.filter(community =>
      community.salesCommunityName.toLowerCase().includes(value.toLowerCase())
    );

    this.drawerCommunityResults.set(results);
  }

  isCommunityDisabled(): boolean {
    const requiresDivisionSelection = this.divisions().length > 1;
    return this.loading || (requiresDivisionSelection && !this.selectedDivision());
  }

  isSearchDisabled(): boolean {
    return this.loading || !this.selectedCommunity();
  }

  isDivisionDisabled(): boolean {
    const hasSingleDivision = this.divisions().length === 1;
    return this.loadingDivisions || hasSingleDivision;
  }

  async onDivisionSelected(division: DivisionDto | undefined): Promise<void> {
    this.selectedDivision.set(division);

    this.selectedCommunity.set(undefined);
    this.drawerSelectedCommunity = undefined;
    this.communitySearchTerm = "";

    this.communities = [];
    this.drawerCommunityResults.set([]);

    if (!division) {
      return;
    }

    await this.loadCommunitiesForDivision(division.externalDivisionId);
  }

  private async loadCommunitiesForDivision(externalDivisionId: number): Promise<void> {
    const communities = await firstValueFrom(
      this.lotSearchService.getSalesCommunitiesByDivision(externalDivisionId)
    );

    this.communities = (communities ?? []).slice().sort((a, b) =>
      a.salesCommunityName.localeCompare(b.salesCommunityName)
    );

    this.drawerCommunityResults.set(this.communities);
  }
}